import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from '../../../global-components/src';
import axios from "axios";
import "./style.css";
import { useMessage } from "../../../global-components/src/index";
import { GoogleBooks } from "../../../types";
import { BUCKET_READBOOKS_URL, READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { errorMessage } from "../../../global-components/src/utils/text";
import { getGoogleBookImage } from "../../../utils/book";
import { BookOpenText, Search } from "lucide-react";
import { isMyLove } from "../../../global-components/src/utils/auth";
import { debounce } from "lodash";

// Tipo unificado — item pode vir do Google Books ou do catálogo próprio
interface UnifiedBookItem {
  id: string;
  source: "google" | "catalog";
  volumeInfo: {
    title: string;
    authors?: string[];
    imageLinks?: { thumbnail?: string };
  };
}

export function SearchGoogleBooks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState<UnifiedBookItem[]>([]);
  const [isSearch, setISearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const { user, token } = useAuth();
  const { setMessage } = useMessage();
  const [termo, setTermo] = useState(searchParams.get("termo") || "");
  const [showFilter, setShowFilter] = useState(false);
  const [termoSearch, setTermoSearch] = useState(termo);
  const navigate = useNavigate();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue.length) setISearch(false);
    setTermo(inputValue);

    debouncedHandleInput(inputValue);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("termo", inputValue);
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
      if(!loading ) handleSearch();
  }, [termoSearch]);

  const debouncedHandleInput = useCallback(debounce(setTermoSearch, 500), [termo]);

  const handleSearch = async () => {
    if (loadingSearch || !termo.length) return;
    setLoadingSearch(true);
    setISearch(true);
    setBooks([]);

    try {
      // Dispara as duas buscas em paralelo
      const [googleRes, catalogRes] = await Promise.allSettled([
        axios.get(`${READBOOKS_API_URL}/google-books/by-name/${encodeURIComponent(termoSearch)}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${READBOOKS_API_URL}/catalog/search?q=${encodeURIComponent(termoSearch)}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const googleItems: UnifiedBookItem[] =
        googleRes.status === "fulfilled"
          ? (googleRes.value.data as GoogleBooks).items?.map((b) => ({
              ...b,
              source: "google" as const,
            })) ?? []
          : [];

      const catalogItems: UnifiedBookItem[] =
        catalogRes.status === "fulfilled"
          ? (catalogRes.value.data.items as UnifiedBookItem[]) ?? []
          : [];

      // Livros da plataforma primeiro, depois Google Books
      setBooks([...catalogItems, ...googleItems]);
    } catch (err: any) {
      setMessage(errorMessage(err), "error");
    } finally {
      setLoadingSearch(false);
    }
  };

  const getBookLink = (book: UnifiedBookItem) =>
    book.source === "catalog"
      ? `/catalogo/${book.id}`        // rota de detalhes do catálogo
      : `/google-livros/${book.id}`;  // rota atual do Google Books

  const getBookCover = (book: UnifiedBookItem) =>
    book.source === "catalog"
      ? `${BUCKET_READBOOKS_URL}/${book.volumeInfo.imageLinks?.thumbnail}` || "/img/book_transparent.png"
      : getGoogleBookImage(book as any);

  return (
    <div className="margin-top-menu container bp-wrapper">

      {/* ── Barra de busca ── */}
      <div className="bp-controls" style={{ marginTop: "24px" }}>
        <div className="bp-search-wrap">
          <Search size={15} className="bp-search-icon" />
          <input
            value={termo}
            onChange={handleChange}
            className="bp-search"
            type="search"
            placeholder="Pesquisar novo livro…"
            aria-label="Search"
          />
        </div>
      </div>

      {/* ── Resultados ── */}
      {loadingSearch ? (
        <div className="bp-grid">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="bp-card bp-skeleton">
              <div className="bp-sk-cover" />
              <div className="bp-sk-body">
                <div className="bp-sk-line bp-sk-line--title" />
                <div className="bp-sk-line bp-sk-line--author" />
                <div className="bp-sk-line bp-sk-line--badge" />
              </div>
            </div>
          ))}
        </div>
      ) : books.length > 0 ? (
        <div className="bp-grid">
          {books.map((book, index) => (
            <Link key={index} className="bp-card" to={getBookLink(book)}>
              <div className="bp-cover-wrap">
                <img
                  onError={(e) => { e.currentTarget.src = "/img/book_transparent.png"; }}
                  src={getBookCover(book)}
                  className="bp-cover"
                  alt={book.volumeInfo.title}
                />
              </div>
              <div className="bp-info">
                <span className="bp-title">{book.volumeInfo.title}</span>
                {book.volumeInfo.authors?.[0] && (
                  <span className="bp-author">{book.volumeInfo.authors[0]}</span>
                )}
                <div className="bp-meta">
                  <span className={`bp-badge bp-badge--${book.source}`}>
                    {book.source === "catalog" ? "Plataforma" : "Google Books"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : !loadingSearch && termo && isSearch ? (
        <div className="bp-empty">
          <BookOpenText size={44} />
          <p>Nenhum livro encontrado</p>
        </div>
      ) : (
        <div className="bp-empty">
          <BookOpenText size={44} />
          <p>Pesquise por um livro para adicionar à leitura</p>
        </div>
      )}
    </div>
  );
}